import { c as _c } from "react/compiler-runtime";
import { CollectionLockup, CollectionLockupAvatar, CollectionLockupContent, CollectionLockupTitle } from "@opensea/app-kit";
import { readFragment } from "@opensea/graphql";
import { Item, ItemContent, ItemSide } from "@opensea/next-ui-kit";
import { classNames, FlexCenter, FlexColumn, NumberDisplay, SkeletonBlock, SpaceBetween, TextBody, TextBodySkeleton, tableRowSizeVariants, tv } from "@opensea/ui-kit";
import { add, isAfter } from "date-fns";
import { CollectionDropStatusChip } from "@/components/collection/CollectionMetadataChips/chips/CollectionDropStatusChip";
import { TokenPrice } from "@/components/price/TokenPrice";
import { ExpandedSearch_collectionFragment } from "../ExpandedSearch.fragment";
import { useExpandedSearchResultSize } from "./hooks";
const {
  container,
  content,
  contentInner,
  statsContainer
} = tv({
  slots: {
    container: "h-16 border-none p-0",
    content: "w-full items-center",
    contentInner: "w-full",
    statsContainer: ""
  }
})();
const randomCollectionNameWidths = ["w-1/2", "w-1/3", "w-2/5", "w-3/5"];
export const mapCollectionToSearchResultItem = collectionKey => {
  const collection = readFragment(ExpandedSearch_collectionFragment, collectionKey);
  return {
    id: collection.id,
    data: collectionKey,
    type: "collection"
  };
};
export function ExpandedSearchCollectionResult(t0) {
  const $ = _c(25);
  const {
    item,
    onSelect,
    className,
    hideStats
  } = t0;
  const collection = readFragment(ExpandedSearch_collectionFragment, item.data);
  const size = useExpandedSearchResultSize();
  const showDropStatus = collection.drop?.stages.length ? isAfter(add(collection.drop.stages[0].startTime, {
    months: 1
  }), Date.now()) : false;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(container(), "hover:bg-transparent", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CollectionLockupAvatar />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CollectionLockupTitle weight="semibold" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== collection || $[5] !== showDropStatus) {
    t4 = showDropStatus ? <CollectionDropStatusChip collection={collection} /> : null;
    $[4] = collection;
    $[5] = showDropStatus;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <FlexCenter className="gap-2">{t3}{t4}</FlexCenter>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== collection.stats || $[10] !== hideStats) {
    t6 = !hideStats && collection.stats.totalSupply > 0 && <TextBody><NumberDisplay className="text-text-secondary" display="quantity" suffix="items" value={collection.stats.totalSupply} /></TextBody>;
    $[9] = collection.stats;
    $[10] = hideStats;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <CollectionLockupContent>{t5}{t6}</CollectionLockupContent>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== collection.floorPrice?.pricePerItem || $[16] !== hideStats) {
    t8 = !hideStats && <ItemSide><TokenPrice className="text-sm" price={collection.floorPrice?.pricePerItem} /></ItemSide>;
    $[15] = collection.floorPrice?.pricePerItem;
    $[16] = hideStats;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== collection || $[19] !== onSelect || $[20] !== size || $[21] !== t1 || $[22] !== t7 || $[23] !== t8) {
    t9 = <CollectionLockup className={t1} collection={collection} onClick={onSelect} size={size}>{t2}{t7}{t8}</CollectionLockup>;
    $[18] = collection;
    $[19] = onSelect;
    $[20] = size;
    $[21] = t1;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}
export function ExpandedSearchCollectionResultSkeleton(t0) {
  const $ = _c(43);
  const {
    index,
    hideStats,
    className
  } = t0;
  const size = useExpandedSearchResultSize();
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== className || $[1] !== index || $[2] !== size) {
    const {
      text
    } = tableRowSizeVariants({
      size
    });
    T4 = Item;
    if ($[13] !== className) {
      t4 = classNames(container(), "px-3", className);
      $[13] = className;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SkeletonBlock className="size-12 shrink-0 rounded" />;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    T3 = ItemContent;
    T2 = SpaceBetween;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = content();
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    T1 = FlexColumn;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = classNames(contentInner(), "w-1/2");
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    T0 = TextBodySkeleton;
    t1 = classNames(text(), randomCollectionNameWidths[index % randomCollectionNameWidths.length]);
    $[0] = className;
    $[1] = index;
    $[2] = size;
    $[3] = T0;
    $[4] = T1;
    $[5] = T2;
    $[6] = T3;
    $[7] = T4;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    T0 = $[3];
    T1 = $[4];
    T2 = $[5];
    T3 = $[6];
    T4 = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
  }
  let t6;
  if ($[18] !== T0 || $[19] !== t1) {
    t6 = <T0 className={t1} />;
    $[18] = T0;
    $[19] = t1;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== hideStats) {
    t7 = !hideStats && <FlexCenter className={statsContainer()}><TextBodySkeleton className="w-[80px]" /></FlexCenter>;
    $[21] = hideStats;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== T1 || $[24] !== t2 || $[25] !== t6 || $[26] !== t7) {
    t8 = <T1 className={t2}>{t6}{t7}</T1>;
    $[23] = T1;
    $[24] = t2;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== hideStats) {
    t9 = !hideStats && <TextBodySkeleton className="w-[100px]" />;
    $[28] = hideStats;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== T2 || $[31] !== t3 || $[32] !== t8 || $[33] !== t9) {
    t10 = <T2 className={t3}>{t8}{t9}</T2>;
    $[30] = T2;
    $[31] = t3;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== T3 || $[36] !== t10) {
    t11 = <T3>{t10}</T3>;
    $[35] = T3;
    $[36] = t10;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== T4 || $[39] !== t11 || $[40] !== t4 || $[41] !== t5) {
    t12 = <T4 className={t4}>{t5}{t11}</T4>;
    $[38] = T4;
    $[39] = t11;
    $[40] = t4;
    $[41] = t5;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}